// ── AR Flow — NOVA INTERFACE: 📦 Avisos do depósito (Meu Dia) ───────────────
// Para o consultor (só as vendas dele) e para o gerente (a loja):
//   🔴 Chegou no depósito e ainda não foi agendado (o depósito vai ligar);
//   📅 Entregas agendadas nos próximos 3 dias;
//   🔴 Observações da entrega para retornar ao cliente ("Falei com o cliente").
// Some sozinho quando não há nada. Não envia nada ao cliente.

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { watchVisiblePolling } from "../visiblePolling.mjs";
import { mayAutoRefresh } from "../nightActivity.mjs";
import { chamarDeposito } from "./DepositoApp";
import { VoiceDictationButton } from "../VoiceDictation";
import LiftCrmClientAction, { LiftCrmClientContext } from "../LiftCrmClientActions";

const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const dm = v => (v ? v.slice(0, 10).split("-").reverse().slice(0, 2).join("/") : "");
const PERIODO = { manha: "manhã", tarde: "tarde", horario: "horário combinado" };

function rotuloAtendimentoCrm(atendimentoCrm) {
  if (!atendimentoCrm || typeof atendimentoCrm !== "object") return "";
  if (atendimentoCrm.state === "conversation") return "Conversa encontrada no CRM";
  if (atendimentoCrm.state === "ambiguous") return "Origem no CRM não confirmada";
  if (atendimentoCrm.state === "unknown") return "Atendimento no CRM não verificado";
  if (atendimentoCrm.state === "unavailable") return "Não foi possível verificar o cadastro no CRM";
  return "";
}

function AvisoLiftCrmClientAction({ item, theme }) {
  const context = useContext(LiftCrmClientContext);
  if (!context) return null;
  return (
    <LiftCrmClientContext.Provider value={{ ...context, canBindOrigin:false }}>
      <LiftCrmClientAction
        storeId={item.storeId}
        pedido={item.pedido}
        sale={item}
        theme={theme}
        noticeInspection={item.atendimentoCrm || null}
      />
    </LiftCrmClientContext.Provider>
  );
}

function PedidoAviso({ item, onAbrirPedido, ariaLabel, style, children, theme }) {
  const atendimento = rotuloAtendimentoCrm(item?.atendimentoCrm);
  return (
    <div style={style}>
      {onAbrirPedido ? (
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={() => onAbrirPedido(item)}
          style={{ display: "block", width: "100%", padding: 0, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", color: "inherit" }}
        >
          {children}
        </button>
      ) : children}
      {atendimento && <div
        data-deposito-crm-atendimento={item.atendimentoCrm.state}
        role="status"
        style={{ marginTop: 5, fontSize: 12, color: item.atendimentoCrm.state === "unavailable" ? "#92400E" : "#64748B" }}
      >{atendimento}</div>}
      {item?.storeId != null && item?.pedido != null
        && !["conversation", "ambiguous", "unknown", "unavailable"].includes(item.atendimentoCrm?.state)
        && <AvisoLiftCrmClientAction item={item} theme={theme} />}
    </div>
  );
}

const avisosEndpoint = user => {
  const query = new URLSearchParams();
  const userId = Number(user?.id);
  if (user?.id != null && user.id !== "" && Number.isInteger(userId) && userId >= 0) {
    query.set("viewAsUserId", String(userId));
  }
  const storeId = Number(user?.storeId);
  if (user?.storeId != null && user.storeId !== "" && Number.isInteger(storeId) && storeId >= 1 && storeId <= 4) {
    query.set("storeId", String(storeId));
  }
  const scope = query.toString();
  return scope ? `/api/deposito/avisos?${scope}` : "/api/deposito/avisos";
};

function Observacao({ th, o, mostrarConsultor, onFeito, onAbrirPedido }) {
  const [abrir, setAbrir] = useState(false);
  const [retorno, setRetorno] = useState("");
  const [erro, setErro] = useState("");
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(null);
  const mountedRef = useRef(true);
  const line = th.nuLine || th.sandDark;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
    };
  }, []);
  const salvar = async () => {
    if (requestRef.current || !retorno.trim()) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(true); setErro("");
    try {
      await chamarDeposito(`/api/deposito/${o.storeId}/${encodeURIComponent(o.pedido)}/observacao/${encodeURIComponent(o.id)}/resolver`, { retorno }, { signal: controller.signal });
      if (!controller.signal.aborted && mountedRef.current) onFeito();
    } catch (e) {
      if (!controller.signal.aborted && mountedRef.current) setErro(e.message);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (mountedRef.current) setBusy(false);
    }
  };
  return (
    <div style={{ borderBottom: `1px solid ${line}`, padding: "10px 16px" }}>
      <PedidoAviso item={o} onAbrirPedido={onAbrirPedido} ariaLabel={`Abrir conversa com ${o.cliente?.nome || "cliente"}, pedido ${o.pedido}`} style={{ padding: 0, color: th.text, fontSize: 14 }} theme={th}
      >
        <div><strong>{o.cliente?.nome}</strong> <span style={{ fontFamily: MONO, fontSize: 11, color: th.muted }}>#{o.pedido}</span>{mostrarConsultor && o.consultor ? <span style={{ fontSize: 12, color: th.muted }}> · {o.consultor}</span> : null}</div>
        <div style={{ fontSize: 14, color: th.text, marginTop: 2 }}>📝 {o.texto} <span style={{ fontSize: 12, color: th.muted }}>({o.porNome}, {dm(o.em)})</span></div>
      </PedidoAviso>
      {!abrir && <button type="button" disabled={busy} onClick={() => setAbrir(true)} style={{ marginTop: 6, background: "none", border: `1px solid ${line}`, borderRadius: 4, padding: "5px 10px", cursor: busy ? "wait" : "pointer", fontFamily: "inherit", fontWeight: 700, color: th.text }}>Falei com o cliente</button>}
      {abrir && (
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <input aria-label="Como foi o retorno" value={retorno} onChange={e => setRetorno(e.target.value)} placeholder="Como foi? Ex.: chegou tudo certo, mandei fotos da mesa lateral" style={{ width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 4, border: `1px solid ${line}`, fontFamily: "inherit", fontSize: 14 }} />
            <VoiceDictationButton value={retorno} onChange={setRetorno} />
          </div>
          <button type="button" disabled={busy || !retorno.trim()} onClick={salvar} style={{ padding: "7px 12px", borderRadius: 4, border: "none", background: th.accent, color: "#fff", fontWeight: 700, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1 }}>{busy ? "Salvando…" : "Salvar"}</button>
        </div>
      )}
      {erro && <div role="alert" style={{ fontSize: 13, color: "#991B1B", marginTop: 4 }}>{erro}</div>}
    </div>
  );
}

export default function DepositoAvisos({ theme, currentUser, onAbrirPedido }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const identity = `${currentUser?.id ?? ""}:${currentUser?.role ?? ""}:${currentUser?.storeId ?? ""}:${(currentUser?.storeIds || []).join(",")}`;
  const endpoint = avisosEndpoint(currentUser);
  const [resposta, setResposta] = useState(null);
  const requestRef = useRef(null);
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const carregar = useCallback(() => {
    if (requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    chamarDeposito(endpoint, undefined, { signal: controller.signal })
      .then(d => {
        if (!controller.signal.aborted && identityRef.current === identity) {
          setResposta({ identity, dados: d, erro: "" });
        }
      })
      .catch(e => {
        if (!controller.signal.aborted && identityRef.current === identity) {
          setResposta(previous => ({
            identity,
            dados: previous?.identity === identity ? previous.dados : null,
            erro: e.message,
          }));
        }
      })
      .finally(() => { if (requestRef.current === controller) requestRef.current = null; });
  }, [endpoint, identity]);
  useEffect(() => {
    // A view-as swap must never briefly show notices from the previous identity.
    setResposta(null);
    carregar();
    const stopPolling = watchVisiblePolling(carregar, 60 * 1000, { initial:false });
    const refreshWhenActive = () => {
      if (document.visibilityState !== "hidden" && mayAutoRefresh()) carregar();
    };
    window.addEventListener("focus", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      stopPolling();
      window.removeEventListener("focus", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [carregar]);
  const dados = resposta?.identity === identity ? resposta.dados : null;
  const erro = resposta?.identity === identity ? resposta.erro : "";
  if (!dados && !erro) return null;
  if (!dados && erro) {
    return <div role="alert" style={{ color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 4, padding: "10px 14px", marginBottom: 16 }}>Não foi possível carregar os avisos do depósito: {erro}</div>;
  }
  const { chegaram = [], agendadas = [], observacoes = [] } = dados;
  const vermelho = chegaram.length + observacoes.length;
  if (!chegaram.length && !agendadas.length && !observacoes.length && !erro) return null;
  const mostrarConsultor = currentUser?.role !== "consultant";
  return (
    <section style={{ background: "#fff", border: `1px solid ${line}`, borderTop: `3px solid ${vermelho ? "#B42318" : "#1D4ED8"}`, borderRadius: 4, marginBottom: 16, maxWidth: 880 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "12px 16px", borderBottom: `1px solid ${line}` }}>
        <h2 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 17, color: th.text }}>📦 Do depósito</h2>
        <span style={{ fontFamily: MONO, fontSize: 14, color: vermelho ? "#B42318" : "#1D4ED8" }}>{vermelho || agendadas.length}</span>
      </div>
      {erro && <div role="alert" style={{ color: "#991B1B", background: "#FEF2F2", padding: "8px 16px" }}>Não foi possível atualizar os avisos: {erro}</div>}
      {observacoes.length > 0 && <div style={{ padding: "8px 16px 0", fontSize: 12, fontWeight: 700, color: "#B42318" }}>RETORNAR AO CLIENTE (observação da entrega)</div>}
      {observacoes.map(o => <Observacao key={`${o.storeId}-${o.pedido}-${o.id}`} th={th} o={o} mostrarConsultor={mostrarConsultor} onFeito={carregar} onAbrirPedido={onAbrirPedido} />)}
      {chegaram.length > 0 && <div style={{ padding: "8px 16px 0", fontSize: 12, fontWeight: 700, color: "#B42318" }}>CHEGOU NO DEPÓSITO · AINDA SEM AGENDAMENTO</div>}
      {chegaram.map(i => (
        <PedidoAviso key={`${i.storeId}-${i.pedido}`} item={i} onAbrirPedido={onAbrirPedido} ariaLabel={`Abrir conversa com ${i.cliente?.nome || "cliente"}, pedido ${i.pedido}`} style={{ borderBottom: `1px solid ${line}`, padding: "10px 16px", fontSize: 14, color: th.text }} theme={th}>
          🔴 <strong>{i.cliente?.nome}</strong> <span style={{ fontFamily: MONO, fontSize: 11, color: th.muted }}>#{i.pedido}</span>{mostrarConsultor && i.consultor ? <span style={{ fontSize: 12, color: th.muted }}> · {i.consultor}</span> : null}
          <div style={{ fontSize: 13, color: th.muted }}>{(i.itens || []).map(x => x.produto).join(" · ")}{i.agendamento ? ` · agendamento vencido (${dm(i.agendamento.data)})` : i.chegouEm ? ` · chegou em ${dm(i.chegouEm)}` : ""}. O depósito liga para agendar; se o cliente te procurar, avise o depósito.</div>
        </PedidoAviso>
      ))}
      {agendadas.length > 0 && <div style={{ padding: "8px 16px 0", fontSize: 12, fontWeight: 700, color: "#1D4ED8" }}>ENTREGAS AGENDADAS (PRÓXIMOS 3 DIAS)</div>}
      {agendadas.map(i => (
        <PedidoAviso key={`${i.storeId}-${i.pedido}`} item={i} onAbrirPedido={onAbrirPedido} ariaLabel={`Abrir conversa com ${i.cliente?.nome || "cliente"}, pedido ${i.pedido}`} style={{ borderBottom: `1px solid ${line}`, padding: "10px 16px", fontSize: 14, color: th.text }} theme={th}>
          📅 <strong>{dm(i.agendamento.data)}</strong> · {PERIODO[i.agendamento.periodo] || i.agendamento.periodo}{i.agendamento.horario ? ` ${i.agendamento.horario}` : ""} · {i.cliente?.nome}{mostrarConsultor && i.consultor ? <span style={{ fontSize: 12, color: th.muted }}> · {i.consultor}</span> : null}
        </PedidoAviso>
      ))}
    </section>
  );
}